import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Trash2, Check, Eye } from 'lucide-react';

const Messages = () => {
  const { API, user } = useApp();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [API]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/messages/${messageId}/read`, {}, { withCredentials: true });
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
      setShowDialog(false);
      setSelectedMessage(null);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    setShowDialog(true);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div>
        <h1 className="font-syne font-bold text-2xl text-white">Contact Messages</h1>
        <p className="text-white/50">View and manage contact form submissions</p>
      </div>

      <Card className="bg-brand-dark border-white/5">
        <CardContent className="p-0">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <Mail className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <h3 className="font-syne text-lg text-white mb-2">No Messages Yet</h3>
              <p className="text-white/50">Contact form submissions will appear here.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-white/50">Status</TableHead>
                  <TableHead className="text-white/50">From</TableHead>
                  <TableHead className="text-white/50">Subject</TableHead>
                  <TableHead className="text-white/50">Date</TableHead>
                  <TableHead className="text-white/50 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map(message => (
                  <TableRow 
                    key={message.id} 
                    className={`border-white/5 hover:bg-white/5 cursor-pointer ${!message.read ? 'bg-white/5' : ''}`}
                    onClick={() => openMessage(message)}
                    data-testid={`message-row-${message.id}`}
                  >
                    <TableCell>
                      {message.read ? (
                        <Badge variant="outline" className="border-white/20 text-white/40">Read</Badge>
                      ) : (
                        <Badge className="bg-brand-red">New</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{message.name}</p>
                        <p className="text-white/40 text-sm">{message.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-white/70 max-w-xs truncate">
                      {message.subject}
                    </TableCell>
                    <TableCell className="text-white/40 text-sm whitespace-nowrap">
                      {formatDate(message.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMessage(message);
                          }}
                          className="text-white/40 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMessage(message.id);
                            }}
                            className="text-white/40 hover:text-brand-red"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Message Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-brand-dark border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-syne text-white">
              {selectedMessage?.subject}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              From: {selectedMessage?.name} ({selectedMessage?.email})
              <br />
              Date: {formatDate(selectedMessage?.created_at)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-brand-charcoal rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-white whitespace-pre-wrap">{selectedMessage?.message}</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => window.location.href = `mailto:${selectedMessage?.email}?subject=Re: ${selectedMessage?.subject}`}
              className="border-white/10 text-white hover:bg-white/5"
            >
              <Mail className="w-4 h-4 mr-2" />
              Reply via Email
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="destructive"
                onClick={() => deleteMessage(selectedMessage?.id)}
                className="bg-brand-red hover:bg-brand-red-light"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
